import { MapPin, Plus, Wrench, StickyNote } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { formatStatus } from "../../helper/constant";
import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { VehicleNoteModal } from "../../components/VehicleNoteModal";
import { formatLocalDate, formatNoteWithDate, formatTimestamp } from "@/utils/dateUtils";
import type { Vehicle } from "../../types/vehicle";

interface ExtendedVehicle extends Vehicle {
  vehicle_trim?: string;
  mileage?: string | number;
  age_calculated?: number;
  vehicle_status?: string;
  vehicle_sub_status?: string;
  // Flat stage fields from /inventory/search/
  stage?: string | null;
  stage2?: string | null;
  stage3?: string | null;
  // Recon note fields from /inventory/search/
  recon_note?: string | null;
  recon_note_user?: string | null;
  recon_note_updated_at?: string | null;
}

interface VehicleItemProps {
  vehicle: ExtendedVehicle;
  setEditingVehicle: (vehicle: ExtendedVehicle) => void;
  setReconRequestVehicle: (vehicle: ExtendedVehicle) => void;
}

const VehicleItem = ({
  vehicle,
  setEditingVehicle,
  setReconRequestVehicle,
}: VehicleItemProps) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showAddNoteModal, setShowAddNoteModal] = useState(false);

  const isSold = vehicle.vehicle_status?.toUpperCase() === "SOLD";

  const handleVehicleClick = () => {
    const url = isSold
      ? `/vehicle/${vehicle.vehicle_inventory_id}?is_sold=yes`
      : `/vehicle/${vehicle.vehicle_inventory_id}`;
    navigate(url, {
      state: { from: window.location.pathname },
    });
  };

  return (
    <>
      <div className="bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden hover:shadow-2xl transition-all duration-300 transform relative">
        {isSold && (
          <>
            {/* Semi-transparent overlay for entire card */}
            <div className="absolute inset-0 bg-black/10 z-[5] pointer-events-none rounded-lg" />
            {/* Prominent SOLD badge */}
            <div className="absolute top-3 right-3 z-10 animate-pulse">
              <div className="bg-gradient-to-r from-red-600 via-red-500 to-red-600 text-white px-6 py-2 rounded-lg shadow-[0_8px_20px_rgba(220,38,38,0.5)] border-4 border-white transform rotate-3">
                <span className="text-xl font-black tracking-widest drop-shadow-lg">SOLD</span>
              </div>
            </div>
          </>
        )}
        <div
          className="p-3 border-b border-gray-100 cursor-pointer hover:bg-gray-50 transition-colors"
          onClick={handleVehicleClick}
        >
          <div className="flex items-center justify-between mb-3 pb-2 border-b border-gray-200">
            <p className="text-sm font-bold text-gray-700 font-mono">
              {vehicle.vin}
            </p>
            <p className="text-lg font-extrabold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
              $
              {Number(vehicle.retail_price).toLocaleString("en-US", {
                minimumFractionDigits: 0,
                maximumFractionDigits: 0,
              })}
            </p>
          </div>

          <div className="flex items-start justify-between gap-3 ">
            <div className="flex-1">
              <h3 className="text-lg font-bold mb-1">
                {vehicle.vehicle_year} {vehicle.make} {vehicle.model}{" "}
                {vehicle.vehicle_trim}
              </h3>
              <div className="flex items-center gap-3 mb-2">
                <p className="text-xs text-gray-500">
                  {vehicle.exterior_color} / {vehicle.interior_color}
                </p>
                <span className="text-xs text-gray-400">•</span>
                <p className="text-xs text-gray-500">
                  {Number(vehicle.mileage || vehicle.miles).toLocaleString("en-US")} miles
                </p>
              </div>
              <p
                className="text-xs text-gray-600 line-clamp-2 min-h-10"
                title={
                  vehicle.recon_note
                    ? [
                      vehicle.recon_note,
                      vehicle.recon_note_user ? `By: ${vehicle.recon_note_user}` : null,
                      vehicle.recon_note_updated_at ? `At: ${formatTimestamp(vehicle.recon_note_updated_at)}` : null,
                    ]
                      .filter(Boolean)
                      .join("\n")
                    : undefined
                }
              >
                {formatNoteWithDate(vehicle.recon_note ?? undefined, vehicle.recon_note_updated_at ?? undefined) === "N/A"
                  ? "No description available"
                  : formatNoteWithDate(vehicle.recon_note ?? undefined, vehicle.recon_note_updated_at ?? undefined)}
              </p>
            </div>
          </div>

          <div className="flex items-center justify-between gap-2 my-3 flex-wrap">
            <div className="flex items-center gap-2">
              {vehicle.age_calculated && (
                <span className="text-sm font-medium text-gray-600">
                  Age: {Math.floor(vehicle.age_calculated)}
                </span>
              )}
              <span className="text-sm font-semibold text-gray-700">
                {formatStatus(vehicle.vehicle_status || vehicle.status)}
                {(vehicle.vehicle_sub_status || vehicle.sub_status) &&
                  <>
                    {" / "}
                    <span>{vehicle.vehicle_sub_status || vehicle.sub_status}</span>
                  </>
                }
              </span>
            </div>
            <div className="flex items-center gap-1.5 text-sm text-gray-600">
              <MapPin size={14} className="text-orange-500" />
              <span className="truncate">
                {vehicle.location || "No location"}
              </span>
              {vehicle.stocked_at && (
                <span className="text-xs text-gray-400">· Stocked At: {vehicle.stocked_at}</span>
              )}
            </div>
          </div>
        </div>

        <div className="p-3 space-y-3">
          <div className="bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 p-3 rounded-lg border border-blue-100">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <p className="text-xs font-medium text-blue-600">Location:</p>
                  <p className="text-sm font-semibold text-gray-800">
                    {vehicle.stage2 || vehicle.stage3 ? (
                      <>
                        {vehicle.stage2 && <span>{vehicle.stage2}</span>}
                        {vehicle.stage2 && vehicle.stage3 && <span> / </span>}
                        {vehicle.stage3 && <span>{vehicle.stage3}</span>}
                      </>
                    ) : (
                      "No stage assigned"
                    )}
                  </p>
                </div>
                {vehicle.eta && (
                  <p className="text-xs text-blue-500">
                    ETA {formatLocalDate(vehicle.eta)}
                  </p>
                )}
              </div>

              <div className="flex flex-col gap-2">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setEditingVehicle(vehicle);
                  }}
                  className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white text-sm font-semibold rounded-lg hover:from-blue-700 hover:to-indigo-700 transition-all shadow-md hover:shadow-lg transform hover:scale-105"
                >
                  <Plus size={16} />
                  Change Location
                </button>

                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setReconRequestVehicle(vehicle);
                  }}
                  className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-orange-600 to-red-600 text-white text-sm font-semibold rounded-lg hover:from-orange-700 hover:to-red-700 transition-all shadow-md hover:shadow-lg transform hover:scale-105"
                >
                  <Wrench size={16} />
                  Recon Request
                </button>

                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowAddNoteModal(true);
                  }}
                  className="flex items-center justify-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white text-sm font-semibold rounded-lg hover:from-purple-700 hover:to-pink-700 transition-all shadow-md hover:shadow-lg transform hover:scale-105"
                >
                  <StickyNote size={16} />
                  Create Note
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <VehicleNoteModal
        open={showAddNoteModal}
        onOpenChange={setShowAddNoteModal}
        vehicleInventoryId={vehicle.vehicle_inventory_id}
        mode="create"
        onSuccess={() => {
          queryClient.invalidateQueries({ queryKey: ["vehicleSearch"] });
        }}
      />
    </>
  );
};

export default VehicleItem;
